import { apiClient } from '@/services/api-client';
import type {
  CustomerSubscriptionAttributes,
  CustomerSubscriptionResource,
  CustomerSubscriptionsResponse,
  SubscriptionStatus,
} from '@/types/customer-subscriptions';

type RawSubscriptionResource = CustomerSubscriptionResource & {
  attributes?: CustomerSubscriptionAttributes & {
    status_label?: string;
    status_color?: string;
    billing_interval?: string | null;
    unit_price?: number | null;
    currency?: string | null;
    currency_code?: string | null;
    unit_price_currency?: string | null;
    current_period_start?: string | null;
    current_period_end?: string | null;
    trial_ends_at?: string | null;
    ends_at?: string | null;
    canceled_at?: string | null;
    is_active?: boolean;
    on_trial?: boolean;
    plan?: {
      id?: string;
      name?: string;
      trial_days?: number | null;
    } | null;
  };
};

type FlatSubscriptionAttributes = Partial<CustomerSubscriptionAttributes> &
  Record<string, unknown> & {
    status_label?: string;
    status_color?: string;
    billing_interval?: string | null;
    unit_price?: number | null;
    currency?: string | null;
    currency_code?: string | null;
    unit_price_currency?: string | null;
    current_period_start?: string | null;
    current_period_end?: string | null;
    trial_ends_at?: string | null;
    ends_at?: string | null;
    canceled_at?: string | null;
    is_active?: boolean;
    on_trial?: boolean;
    plan?: {
      id?: string;
      name?: string;
      trial_days?: number | null;
    } | null;
};

type CustomerSubscriptionsMeta = {
  defaultCurrency?: string | null;
  default_currency?: string | null;
} & Record<string, unknown>;

const readString = (value: unknown) => (typeof value === 'string' && value.trim().length > 0 ? value : null);

function normalizeMeta(meta: Record<string, unknown> | undefined): CustomerSubscriptionsMeta | undefined {
  if (!meta) {
    return undefined;
  }

  const defaultCurrency = readString(meta.defaultCurrency) ?? readString(meta.default_currency);
  if (!defaultCurrency) {
    return { ...meta };
  }

  return {
    ...meta,
    defaultCurrency,
  };
}

function normalizeAttributes(rawInput: RawSubscriptionResource['attributes'] | FlatSubscriptionAttributes | undefined): CustomerSubscriptionAttributes {
  const raw = rawInput ?? ({} as FlatSubscriptionAttributes);

  const status = (raw.status as SubscriptionStatus | undefined) ?? 'active';
  const currency =
    readString(raw.currency) ??
    readString(raw.currency_code) ??
    readString(raw.unit_price_currency);

  return {
    status,
    statusLabel: raw.statusLabel ?? raw.status_label ?? status,
    statusColor: raw.statusColor ?? (raw.status_color as CustomerSubscriptionAttributes['statusColor']) ?? 'secondary',
    plan: raw.plan
      ? {
          id: raw.plan.id ?? '',
          name: raw.plan.name ?? '',
          trialDays: raw.plan.trial_days ?? raw.plan.trialDays ?? null,
        }
      : null,
    billingInterval: raw.billingInterval ?? raw.billing_interval ?? null,
    unitPrice: raw.unitPrice ?? raw.unit_price ?? null,
    currency,
    quantity: raw.quantity ?? null,
    currentPeriodStart: raw.currentPeriodStart ?? raw.current_period_start ?? null,
    currentPeriodEnd: raw.currentPeriodEnd ?? raw.current_period_end ?? null,
    trialEndsAt: raw.trialEndsAt ?? raw.trial_ends_at ?? null,
    endsAt: raw.endsAt ?? raw.ends_at ?? null,
    canceledAt: raw.canceledAt ?? raw.canceled_at ?? null,
    isActive: Boolean(raw.isActive ?? raw.is_active),
    onTrial: Boolean(raw.onTrial ?? raw.on_trial),
  };
}

function normalizeResource(resource: RawSubscriptionResource | (FlatSubscriptionAttributes & { id: string; type?: string; links?: { self?: string } })): CustomerSubscriptionResource {
  const attributesInput =
    (resource as RawSubscriptionResource).attributes ?? (resource as FlatSubscriptionAttributes | undefined);

  return {
    id: resource.id,
    type: resource.type ?? 'subscriptions',
    attributes: normalizeAttributes(attributesInput),
    links: resource.links,
  };
}

class CustomerSubscriptionsService {
  async list(status?: SubscriptionStatus[]): Promise<CustomerSubscriptionsResponse> {
    const params = new URLSearchParams();
    if (status?.length) {
      status.forEach((value) => params.append('status[]', value));
    }

    const query = params.toString();
    const url = `/api/v1/customer/subscriptions${query ? `?${query}` : ''}`;

    const response = await apiClient.get<CustomerSubscriptionsResponse>(url);
    const payload = response.data;
    const data = Array.isArray(payload)
      ? payload
      : (payload as CustomerSubscriptionsResponse | undefined)?.data ?? [];
    const links = !Array.isArray(payload)
      ? (payload as CustomerSubscriptionsResponse | undefined)?.links
      : undefined;
    const metaSource =
      (!Array.isArray(payload)
        ? (payload as { meta?: Record<string, unknown> } | undefined)?.meta
        : undefined) ?? response.meta;
    const meta = normalizeMeta(metaSource);

    return {
      data: data.map((item) => normalizeResource(item as RawSubscriptionResource)),
      ...(links ? { links } : {}),
      ...(meta ? { meta } : {}),
    };
  }
}

export const customerSubscriptionsService = new CustomerSubscriptionsService();
